// Inline editor — switches on section.kind and returns a form to mutate `section`.
import { useState, useEffect } from "react";
import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/components/prism-markup";
import "prismjs/components/prism-css";
import "prismjs/components/prism-javascript";
import "prismjs/themes/prism-tomorrow.css";
import { toast } from "sonner";
import api from "@/lib/api";
import ImagePicker from "@/components/ImagePicker";
import { ICONS, arr } from "./constants";
import { F, ListEditor } from "./fields";
import { CODE_PRESETS } from "./codePresets";
import { splitHtmlDocument } from "@/lib/htmlSplit";

export function SectionEditor({ section, onChange }) {
  const s = section || {};
  const set = (patch) => onChange({ ...s, ...patch });
  switch (s.kind) {
    case "hero":
      return (
        <div className="space-y-3">
          <F label="Title" value={s.title} onChange={(v) => set({ title: v })} />
          <F label="Subtitle" textarea value={s.subtitle} onChange={(v) => set({ subtitle: v })} />
          <F label="CTA button" value={s.cta} onChange={(v) => set({ cta: v })} />
          <ImagePicker label="Hero image" value={s.image} onChange={(v) => set({ image: v })} testid="hero-image" />
        </div>
      );
    case "stats":
      return <ListEditor label="Stats" items={s.items} onChange={(items) => set({ items })}
        fields={[{ key: "n", label: "Number" }, { key: "l", label: "Label" }]} />;
    case "features":
      return (
        <div className="space-y-3">
          <F label="Section title" value={s.title} onChange={(v) => set({ title: v })} />
          <ListEditor label="Features" items={s.items} onChange={(items) => set({ items })}
            fields={[{ key: "t", label: "Title" }, { key: "d", label: "Description", textarea: true }]} />
        </div>
      );
    case "testimonials":
      return <ListEditor label="Testimonials" items={s.items} onChange={(items) => set({ items })}
        fields={[{ key: "q", label: "Quote", textarea: true }, { key: "n", label: "Name" }, { key: "r", label: "Role" }]} />;
    case "cta":
      return (
        <div className="space-y-3">
          <F label="Title" value={s.title} onChange={(v) => set({ title: v })} />
          <F label="Subtitle" textarea value={s.subtitle} onChange={(v) => set({ subtitle: v })} />
          <F label="Button text" value={s.button} onChange={(v) => set({ button: v })} />
        </div>
      );
    case "faq":
      return <ListEditor label="FAQs" items={s.items} onChange={(items) => set({ items })}
        fields={[{ key: "q", label: "Question" }, { key: "a", label: "Answer", textarea: true }]} />;
    case "about":
      return (
        <div className="space-y-3">
          <F label="Section title" value={s.title} onChange={(v) => set({ title: v })} />
          <F label="Body" textarea value={s.body} onChange={(v) => set({ body: v })} />
        </div>
      );
    case "pricing":
      return <ListEditor label="Plans" items={s.plans} onChange={(plans) => set({ plans })}
        fields={[{ key: "name", label: "Name" }, { key: "price", label: "Price" }]} />;
    case "gallery":
      return (
        <div className="space-y-3">
          <div className="vp-label" style={{ fontSize: "0.65rem" }}>Gallery images</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {(s.images || []).map((url, i) => (
              <ImagePicker
                key={i}
                label={`Image #${i + 1}`}
                value={url}
                onChange={(next) => {
                  const arr = [...(s.images || [])];
                  if (!next) arr.splice(i, 1);
                  else arr[i] = next;
                  set({ images: arr });
                }}
                testid={"gallery-img-" + i}
              />
            ))}
            <div className="vp-card p-4">
              <ImagePicker
                label="Add image"
                value=""
                onChange={(next) => next && set({ images: [...(s.images || []), next] })}
                testid={"gallery-img-add"}
              />
            </div>
          </div>
        </div>
      );
    case "waitlist":
      return (
        <div className="space-y-3">
          <F label="Title" value={s.title} onChange={(v) => set({ title: v })} />
          <F label="Subtitle" textarea value={s.subtitle} onChange={(v) => set({ subtitle: v })} />
          <F label="Button text" value={s.button} onChange={(v) => set({ button: v })} />
          <F label="Success message" value={s.success_message} onChange={(v) => set({ success_message: v })} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!s.collect_name} onChange={(e) => set({ collect_name: e.target.checked })} />
            <span>Also collect the subscriber's name</span>
          </label>
        </div>
      );
    case "registration_form": {
      const fields = s.fields || { name: true, mobile: true, email: false, city: true, requirement: false };
      const setField = (k, v) => set({ fields: { ...fields, [k]: v } });
      return (
        <div className="space-y-3" data-testid="reg-form-editor">
          <F label="Title" value={s.title} onChange={(v) => set({ title: v })} />
          <F label="Subtitle" textarea value={s.subtitle} onChange={(v) => set({ subtitle: v })} />
          <F label="Submit button text" value={s.button} onChange={(v) => set({ button: v })} />
          <div className="vp-card p-3">
            <div className="text-xs font-bold uppercase tracking-widest text-[#747680] mb-2">Fields to collect</div>
            {[
              ["name", "Name (required)"],
              ["mobile", "Mobile / WhatsApp"],
              ["email", "Email"],
              ["city", "City"],
              ["requirement", "Requirement / message"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm py-1">
                <input type="checkbox" checked={!!fields[k]} disabled={k === "name"}
                  onChange={(e) => setField(k, e.target.checked)} data-testid={`reg-toggle-${k}`} />
                <span>{label}</span>
              </label>
            ))}
          </div>
          <div className="vp-card p-3 space-y-2">
            <div className="text-xs font-bold uppercase tracking-widest text-[#747680]">After submit</div>
            <F label="Thank you title" value={s.success_title} onChange={(v) => set({ success_title: v })} />
            <F label="Thank you message" textarea value={s.success_message} onChange={(v) => set({ success_message: v })} />
            <F label="WhatsApp number (with country code)" placeholder="+91 98765 43210"
              value={s.whatsapp_number} onChange={(v) => set({ whatsapp_number: v })} />
            <F label="Call number (with country code)" placeholder="+91 98765 43210"
              value={s.call_number} onChange={(v) => set({ call_number: v })} />
          </div>
          <div className="text-xs text-[#747680] leading-relaxed">
            Submissions automatically create a lead in your CRM (Leads section) and email the workspace owner + platform admin.
          </div>
        </div>
      );
    }
    case "countdown":
      return (
        <div className="space-y-3">
          <F label="Title" value={s.title} onChange={(v) => set({ title: v })} />
          <label className="block">
            <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Target date & time</span>
            <input
              type="datetime-local"
              className="vp-input text-sm"
              value={s.target ? new Date(s.target).toISOString().slice(0, 16) : ""}
              onChange={(e) => set({ target: e.target.value ? new Date(e.target.value).toISOString() : null })}
            />
          </label>
        </div>
      );

    // ---- NEW SECTION EDITORS ----
    case "youtube_video":
      return (
        <div className="space-y-3">
          <F label="Title (optional)" value={s.title} onChange={(v) => set({ title: v })} />
          <F label="YouTube URL" placeholder="https://www.youtube.com/watch?v=..." value={s.url} onChange={(v) => set({ url: v })} />
          <div className="text-xs text-[#747680]">Any YouTube URL works — regular, share, or embed. We auto-detect the video id.</div>
        </div>
      );
    case "video_embed":
      return (
        <div className="space-y-3">
          <F label="Title (optional)" value={s.title} onChange={(v) => set({ title: v })} />
          <F label="MP4 URL" placeholder="https://cdn.example.com/video.mp4" value={s.url} onChange={(v) => set({ url: v })} />
          <F label="Poster image URL (optional)" placeholder="https://…jpg" value={s.poster} onChange={(v) => set({ poster: v })} />
        </div>
      );
    case "instagram_embed":
      return (
        <div className="space-y-3">
          <F label="Title" value={s.title} onChange={(v) => set({ title: v })} />
          <F label="Instagram handle" placeholder="@yourhandle" value={s.handle} onChange={(v) => set({ handle: v })} />
          <F label="Instagram URL" placeholder="https://instagram.com/yourhandle" value={s.url} onChange={(v) => set({ url: v })} />
        </div>
      );
    case "button":
      return (
        <div className="space-y-3">
          <F label="Section title (optional)" value={s.title} onChange={(v) => set({ title: v })} />
          <F label="Button label" value={s.label} onChange={(v) => set({ label: v })} />
          <F label="URL (leave blank for #cta anchor)" placeholder="https://…" value={s.url} onChange={(v) => set({ url: v })} />
          <label className="block">
            <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>Style</span>
            <select className="vp-input text-sm" value={s.style || "solid"} onChange={(e) => set({ style: e.target.value })}>
              <option value="solid">Solid (filled)</option>
              <option value="outline">Outline</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!s.new_tab} onChange={(e) => set({ new_tab: e.target.checked })} />
            <span>Open in new tab</span>
          </label>
        </div>
      );
    case "logos_strip":
      return (
        <div className="space-y-3">
          <F label="Title" value={s.title} onChange={(v) => set({ title: v })} />
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="vp-label" style={{ fontSize: "0.65rem" }}>Logos / Company names</div>
              <button type="button" onClick={() => set({ items: [...arr(s.items), "New company"] })} className="text-xs text-[#0B5CFF] hover:underline">+ Add</button>
            </div>
            <div className="space-y-2">
              {arr(s.items).map((n, i) => (
                <div key={i} className="flex gap-2">
                  <input className="vp-input text-sm flex-1" value={n} onChange={(e) => {
                    const next = [...arr(s.items)]; next[i] = e.target.value; set({ items: next });
                  }} />
                  <button type="button" onClick={() => {
                    const next = arr(s.items).filter((_, x) => x !== i); set({ items: next });
                  }} className="text-xs text-[#B00020] px-2">Remove</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    case "contact_info":
      return (
        <div className="space-y-3">
          <F label="Title" value={s.title} onChange={(v) => set({ title: v })} />
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="vp-label" style={{ fontSize: "0.65rem" }}>Contact items</div>
              <button type="button" onClick={() => set({ items: [...arr(s.items), { kind: "email", value: "" }] })} className="text-xs text-[#0B5CFF] hover:underline">+ Add</button>
            </div>
            <div className="space-y-2">
              {arr(s.items).map((it, i) => (
                <div key={i} className="vp-card p-3 space-y-2">
                  <div className="flex gap-2">
                    <select className="vp-input text-sm w-32" value={it.kind || "email"} onChange={(e) => {
                      const next = [...arr(s.items)]; next[i] = { ...next[i], kind: e.target.value }; set({ items: next });
                    }}>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="phone">Phone</option>
                      <option value="email">Email</option>
                      <option value="address">Address</option>
                    </select>
                    <input className="vp-input text-sm flex-1" placeholder="+91… / hello@… / address" value={it.value || ""} onChange={(e) => {
                      const next = [...arr(s.items)]; next[i] = { ...next[i], value: e.target.value }; set({ items: next });
                    }} />
                  </div>
                  <button type="button" onClick={() => set({ items: arr(s.items).filter((_, x) => x !== i) })} className="text-xs text-[#B00020] hover:underline">Remove</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    case "steps":
      return (
        <div className="space-y-3">
          <F label="Section title" value={s.title} onChange={(v) => set({ title: v })} />
          <ListEditor label="Steps" items={s.items} onChange={(items) => set({ items })}
            fields={[
              { key: "n", label: "Number / label (1, 2, 3…)" },
              { key: "t", label: "Step title" },
              { key: "d", label: "Description", textarea: true },
            ]} />
        </div>
      );
    case "image_text":
      return (
        <div className="space-y-3">
          <F label="Title" value={s.title} onChange={(v) => set({ title: v })} />
          <F label="Body" textarea value={s.body} onChange={(v) => set({ body: v })} />
          <ImagePicker label="Side image" value={s.image} onChange={(v) => set({ image: v })} testid="image-text-img" />
          <F label="CTA button (optional)" value={s.cta} onChange={(v) => set({ cta: v })} />
          <F label="CTA URL (optional)" value={s.cta_url} onChange={(v) => set({ cta_url: v })} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!s.reverse} onChange={(e) => set({ reverse: e.target.checked })} />
            <span>Reverse layout (image on right)</span>
          </label>
        </div>
      );
    case "text_block":
      return (
        <div className="space-y-3">
          <F label="Title (optional)" value={s.title} onChange={(v) => set({ title: v })} />
          <F label="Body" textarea value={s.body} onChange={(v) => set({ body: v })} />
        </div>
      );
    case "custom_code":
      return <CustomCodeEditor s={s} set={set} />;

    default:
      return <div className="text-xs text-[#747680]">No editor for this section kind.</div>;
  }
}

function CustomCodeEditor({ s, set }) {
  const [tab, setTab] = useState("html");
  const [presetOpen, setPresetOpen] = useState(false);
  const [mySnippets, setMySnippets] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Load only custom_code snippets — these are the "reusable code blocks" the user has saved.
    api.get("/section-snippets").then((r) => {
      const codeOnes = (r.data || []).filter((sn) => sn.section?.kind === "custom_code");
      setMySnippets(codeOnes);
    }).catch(() => {});
  }, []);

  const tabs = [
    { k: "html", label: "HTML", lang: Prism.languages.markup,     placeholder: "<div>Your markup…</div>" },
    { k: "css",  label: "CSS",  lang: Prism.languages.css,        placeholder: "body { background: #fafafa; }" },
    { k: "js",   label: "JS",   lang: Prism.languages.javascript, placeholder: "console.log('hi')" },
  ];
  const active = tabs.find((t) => t.k === tab);

  const applyPreset = (preset) => {
    if (!window.confirm(`Replace current code with the "${preset.label}" preset?`)) return;
    set({ ...preset.patch });
    setPresetOpen(false);
  };

  const saveAsSnippet = async () => {
    const name = window.prompt("Name this code snippet (it appears in the ✨ menu next time):");
    if (!name) return;
    setSaving(true);
    try {
      const { data } = await api.post("/section-snippets", {
        name,
        section: { kind: "custom_code", html: s.html || "", css: s.css || "", js: s.js || "", height: s.height ?? null },
      });
      setMySnippets((prev) => [data, ...prev]);
      toast.success("Snippet saved to your workspace library");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const applyMySnippet = (snip) => {
    if (!window.confirm(`Replace current code with your "${snip.name}" snippet?`)) return;
    const sec = snip.section || {};
    set({ html: sec.html || "", css: sec.css || "", js: sec.js || "", height: sec.height ?? null });
    setPresetOpen(false);
  };

  const deleteMySnippet = async (snip, ev) => {
    ev.stopPropagation();
    if (!window.confirm(`Delete "${snip.name}" from your workspace library?`)) return;
    try {
      await api.delete(`/section-snippets/${snip.id}`);
      setMySnippets((prev) => prev.filter((x) => x.id !== snip.id));
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-3" data-testid="custom-code-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1 rounded-lg bg-[#F5F5F7] p-1">
          {tabs.map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              className={"px-3 py-1.5 text-xs font-bold rounded-md transition-colors " +
                (tab === t.k ? "bg-white text-[#0B0D17] shadow-sm" : "text-[#4A4D57] hover:text-[#0B0D17]")}
              data-testid={`custom-code-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              const raw = window.prompt("Full HTML paste karein — hum automatically <style> aur <script> alag kar denge (existing code overwrite hoga):");
              if (!raw) return;
              const { html, css, js, head_extras } = splitHtmlDocument(raw);
              if (!html && !css && !js) { toast.error("HTML parse nahi hua"); return; }
              set({ html: html || raw, css: css || "", js: js || "", head_extras: head_extras || "" });
              toast.success(`Split: HTML ${html.length} · CSS ${css.length} · JS ${js.length} chars`);
            }}
            className="text-xs inline-flex items-center gap-1.5 py-1.5 px-3 rounded-lg border border-[#0B5CFF] bg-[#0B5CFF]/5 hover:bg-[#0B5CFF]/10 text-[#0B5CFF] font-semibold"
            data-testid="custom-code-paste-html"
            title="Paste a full HTML document — auto splits <style>/<script>"
          >
            📋 Paste full HTML
          </button>
          <button
            type="button"
            disabled={saving || !(s.html || s.css || s.js)}
            onClick={saveAsSnippet}
            className="text-xs inline-flex items-center gap-1.5 py-1.5 px-3 rounded-lg border border-[#E4E5E9] bg-white hover:border-[#0B5CFF] text-[#0B0D17] font-semibold disabled:opacity-50"
            data-testid="custom-code-save-snippet"
          >
            💾 {saving ? "Saving…" : "Save as snippet"}
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setPresetOpen((v) => !v)}
              className="text-xs inline-flex items-center gap-1.5 py-1.5 px-3 rounded-lg border border-[#E4E5E9] bg-white hover:border-[#0B5CFF] text-[#0B0D17] font-semibold"
              data-testid="custom-code-preset-btn"
            >
              ✨ Insert snippet ▾
            </button>
            {presetOpen && (
              <div className="absolute right-0 top-full mt-1 w-72 rounded-xl bg-white border border-[#E4E5E9] shadow-xl z-30 p-2 max-h-96 overflow-y-auto">
                {mySnippets.length > 0 && (
                  <>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#747680] px-3 py-1.5">Your saved snippets</div>
                    {mySnippets.map((sn) => (
                      <button
                        key={sn.id}
                        type="button"
                        onClick={() => applyMySnippet(sn)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#F5F5F7] flex items-center justify-between gap-2 group"
                        data-testid={`custom-code-mysnip-${sn.id}`}
                      >
                        <span className="text-sm text-[#0B0D17] truncate">{sn.name}</span>
                        <span
                          onClick={(e) => deleteMySnippet(sn, e)}
                          role="button"
                          className="text-[#B00020] text-xs opacity-0 group-hover:opacity-100 hover:underline"
                          title="Delete"
                        >delete</span>
                      </button>
                    ))}
                    <div className="border-t border-[#E4E5E9] my-1"></div>
                  </>
                )}
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#747680] px-3 py-1.5">Built-in library</div>
                {CODE_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => applyPreset(p)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#F5F5F7] flex items-center justify-between gap-2"
                    data-testid={`custom-code-preset-${p.id}`}
                  >
                    <span className="text-sm text-[#0B0D17]">{p.label}</span>
                    <span className="text-[10px] uppercase tracking-widest font-bold text-[#747680]">{p.tag}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        className="rounded-lg border border-[#E4E5E9] bg-[#0F1220] overflow-auto"
        style={{ maxHeight: "440px" }}
      >
        <Editor
          value={s[tab] || ""}
          onValueChange={(code) => set({ [tab]: code })}
          highlight={(code) => Prism.highlight(code || "", active.lang, active.label.toLowerCase())}
          padding={14}
          placeholder={active.placeholder}
          textareaId={`custom-code-textarea-${tab}`}
          textareaClassName="focus:outline-none"
          data-testid={`custom-code-textarea-${tab}`}
          style={{
            fontFamily: '"Fira Code", "SF Mono", Menlo, Consolas, monospace',
            fontSize: 13,
            color: "#E4E5E9",
            minHeight: 260,
            tabSize: 2,
          }}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs text-[#4A4D57]">
          Pinned height (px, blank = auto)
          <input
            type="number"
            min="80"
            value={s.height ?? ""}
            onChange={(e) => set({ height: e.target.value ? Number(e.target.value) : null })}
            placeholder="Auto-fit"
            className="mt-1 w-full h-9 px-2 rounded-md border border-[#E4E5E9] text-sm"
          />
        </label>
        <div className="text-xs text-[#747680] flex items-end pb-1">
          Runs in a <b className="mx-1">sandboxed iframe</b> — your JS can't touch the rest of the page.
        </div>
      </div>
    </div>
  );
}
